import { 
  collection, 
  query, 
  where, 
  getDocs, 
  doc, 
  getDoc, 
  setDoc, 
  serverTimestamp,
  updateDoc
} from "firebase/firestore";
import { db } from "../../firebase/firebase";

// Helper to generate Shop ID
const generateShopId = (shopName) => {
  const prefix = shopName ? shopName.replace(/[^a-zA-Z0-9]/g, '').substring(0, 3).toUpperCase() : "SHO";
  const timestamp = Date.now();
  const random = Math.floor(1000 + Math.random() * 9000); // 4 digit random
  return `SHOP_${prefix}_${timestamp}_${random}`;
};

// Check if seller exists by phone number
export const checkSellerExistsByPhone = async (phoneNumber) => {
  try {
    // Run both queries in parallel for better performance
    const [querySnapshot1, querySnapshot2] = await Promise.all([
      getDocs(query(collection(db, "sellers"), where("userPhone", "==", phoneNumber))),
      getDocs(query(collection(db, "sellers"), where("phone", "==", phoneNumber)))
    ]);

    return !querySnapshot1.empty || !querySnapshot2.empty;
  } catch (error) {
    console.error("Error checking seller by phone:", error);
    return false;
  }
};

// Check if seller exists by email
export const checkSellerExistsByEmail = async (email) => {
  try {
    const q = query(collection(db, "sellers"), where("email", "==", email));
    const querySnapshot = await getDocs(q);
    return !querySnapshot.empty;
  } catch (error) {
    console.error("Error checking seller by email:", error);
    return false;
  }
};

// Get seller email by phone number
export const getSellerEmailByPhone = async (phoneNumber) => {
  try {
    const formattedPhone = phoneNumber.startsWith("+") ? phoneNumber : `+91${phoneNumber}`;
    
    // Try userPhone first (current structure)
    const q1 = query(collection(db, "sellers"), where("userPhone", "==", formattedPhone));
    const querySnapshot1 = await getDocs(q1);
    if (!querySnapshot1.empty) {
      const sellerData = querySnapshot1.docs[0].data();
      return sellerData.email || null;
    }
    
    // Fallback to phone (legacy support)
    const q2 = query(collection(db, "sellers"), where("phone", "==", formattedPhone));
    const querySnapshot2 = await getDocs(q2);
    if (!querySnapshot2.empty) {
      const sellerData = querySnapshot2.docs[0].data();
      return sellerData.email || null;
    }
    
    return null;
  } catch (error) {
    console.error("Error getting seller email by phone:", error);
    return null;
  }
};

// Check if seller exists by userId
export const checkSellerByUserId = async (userId) => {
  try {
    // First try to find by userId field
    const q = query(collection(db, "sellers"), where("userId", "==", userId));
    const querySnapshot = await getDocs(q);
    
    if (!querySnapshot.empty) {
      const sellerDoc = querySnapshot.docs[0];
      return { seller: { id: sellerDoc.id, ...sellerDoc.data() }, error: null };
    }
    
    // Fallback: Check if document ID matches userId (legacy structure)
    const docRef = doc(db, "sellers", userId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      return { seller: { id: docSnap.id, ...docSnap.data() }, error: null };
    }
    
    return { seller: null, error: null };
  } catch (error) {
    console.error("Error checking seller:", error);
    return { seller: null, error: error.message };
  }
};

// Create a new seller
export const createSeller = async (userId, data) => {
  try {
    const shopNameClean = data.shopName || data.sellerName || "";
    const sellerNameClean = data.sellerName || data.shopName || "";
    const shopId = data.shopId || generateShopId(shopNameClean);

    const formattedPhone = data.phoneNumber 
      ? (data.phoneNumber.startsWith("+") ? data.phoneNumber : `+91${data.phoneNumber}`)
      : (data.userPhone || "");

    const sellerData = {
      autoVerified: true,
      businessAddress: data.pickupAddress || data.businessAddress || "",
      businessName: data.businessName || null,
      displayName: shopNameClean || sellerNameClean,
      email: data.email || "",
      followerCount: 0,
      gstin: data.gstin || null,
      isVerified: true,
      profileImage: data.profileImage || "",
      sellerName: sellerNameClean,
      shopId: shopId,
      shopImage: data.shopImage || "",
      shopName: shopNameClean,
      soldCount: 0,
      status: "approved",
      submittedAt: serverTimestamp(),
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      userId: userId,
      userPhone: formattedPhone,
      phone: formattedPhone,
      verificationStatus: "verified",
      verifiedAt: serverTimestamp()
    };

    await setDoc(doc(db, "sellers", userId), sellerData);
    
    return { id: userId, success: true, error: null };
  } catch (error) {
    console.error("Error creating seller:", error);
    return { id: null, success: false, error: error.message };
  }
};


// Ensure user document exists in 'users' collection
export const ensureUserDocument = async (uid, phoneNumber, email, sellerName = "") => {
  try {
    const userRef = doc(db, "users", uid);
    
    // Prepare base user data
    const userData = {
      uid,
      phoneNumber,
      mobileNumber: phoneNumber,
      email: email || null,
      isSeller: true,
      profileComplete: true,
      registrationCompleted: true,
      updatedAt: serverTimestamp()
    };

    // Only set displayName if sellerName is provided, to avoid overwriting with empty string
    // If sellerName is provided, we definitely want to use it as displayName
    if (sellerName && sellerName.trim() !== "") {
      userData.displayName = sellerName.trim();
    } else {
      // If no sellerName passed, we might want to check if it exists in DB, 
      // but for "ensure" usually we merge. 
      // If we don't include displayName in userData, merge: true will keep existing.
      // But if it's a NEW user, we need a default.
      // We can handle this by fetching first, or just relying on merge.
    }

    // However, the user specifically said: "making display nmae empty... use seller name"
    // So if I am creating a user, I MUST have sellerName.
    
    const userSnap = await getDoc(userRef);
    if (!userSnap.exists()) {
      userData.createdAt = serverTimestamp();
      // For new user, if sellerName is empty, try to set a fallback or leave it undefined?
      // User requested "use seller name to disply name".
      if (!userData.displayName && sellerName) {
         userData.displayName = sellerName;
      }
    } else {
      // User exists. If sellerName was passed, we update displayName (already handled above).
      // If sellerName was NOT passed (empty), we do NOT add it to userData, so it doesn't overwrite existing.
    }

    await setDoc(userRef, userData, { merge: true });

    return { success: true, error: null };
  } catch (error) {
    console.error("Error ensuring user document:", error);
    return { success: false, error: error.message };
  }
};

// Get seller by ID
export const getSeller = async (sellerId) => {
  try {
    const docRef = doc(db, "sellers", sellerId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      return { id: docSnap.id, ...docSnap.data() };
    }
    return null;
  } catch (error) {
    console.error("Error getting seller:", error);
    return null;
  }
};

// Update seller data
export const updateSeller = async (sellerId, data) => {
  try {
    const docRef = doc(db, "sellers", sellerId);
    
    // If updating seller name, we should also update user display name?
    // The user mentioned "editing users collection for seller".
    // This function updates 'sellers' collection.
    // We should probably update 'users' collection too if sellerName is present.
    
    await updateDoc(docRef, {
      ...data,
      updatedAt: serverTimestamp()
    });
    
    // If sellerName is updated, sync to users collection
    if (data.sellerName) {
        // We need userId. Ideally it's same as sellerId in our new structure (doc(db, "sellers", userId))
        // But let's check if the doc ID is indeed the userId.
        // The createSeller uses setDoc(doc(db, "sellers", userId), ...), so sellerId IS userId.
        const userRef = doc(db, "users", sellerId);
        await updateDoc(userRef, {
            displayName: data.sellerName,
            updatedAt: serverTimestamp()
        }).catch(err => console.log("Could not sync to user doc:", err));
    }

    return { success: true, error: null };
  } catch (error) {
    console.error("Error updating seller:", error);
    return { success: false, error: error.message };
  }
};
